import React from 'react';
import { BusinessIdeaCard, TaskCard, ParticleBackground } from '../components';
import { useBusinessIdea } from '../hooks/useBusinessIdea';
import { useNextTasks } from '../hooks/useNextTask';
import { useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import { RocketLaunch } from '@mui/icons-material';

const HomePage: React.FC = () => {
  const { businessIdea, loading, error } = useBusinessIdea();
  const { nextTasks, loading: nextTasksLoading } = useNextTasks(6); // Show up to 6 next tasks
  const navigate = useNavigate();

  const handleTaskClick = (taskId: string) => {
    navigate(`/task/${taskId}`);
  };

  const handleStartOnboarding = () => {
    navigate('/onboarding');
  };

  // Simple empty state for users without business ideas
  const renderEmptyState = () => (
    <div className="mb-8 flex justify-center">
      <div className="max-w-md w-full">
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 p-8 text-center shadow-xl">
          {/* Icon Container */}
          <div className="w-20 h-20 mx-auto mb-6 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
            <RocketLaunch sx={{ fontSize: 32, color: 'white' }} />
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-white mb-4">No Linked Business Found</h2>

          {/* Description */}
          <p className="text-gray-400 mb-8 leading-relaxed">
            You'll need to go through onboarding to get started with your business idea. This will
            guide you through creating your first business opportunity.
          </p>

          {/* Button */}
          <button
            onClick={handleStartOnboarding}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl"
          >
            Start Onboarding
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background-dark">
      <ParticleBackground star />
      <main className="p-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-bold font-heading text-white mb-6">My Business</h1>

          {/* Business Idea Card */}
          {loading && (
            <div className="mb-8">
              <div className="bg-background-surface/95 backdrop-blur-sm rounded-lg border border-gray-600 p-6">
                <div className="animate-pulse">
                  <div className="h-6 bg-gray-700 rounded w-3/4 mb-4"></div>
                  <div className="h-4 bg-gray-700 rounded w-full mb-2"></div>
                  <div className="h-4 bg-gray-700 rounded w-5/6 mb-4"></div>
                  <div className="h-16 bg-gray-700 rounded w-full"></div>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-8 bg-red-900/20 backdrop-blur-sm border border-red-500 rounded-lg p-6">
              <p className="text-red-400">Failed to load your business idea: {error}</p>
              <Button
                variant="outlined"
                onClick={() => window.location.reload()}
                className="mt-3 border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
              >
                Try Again
              </Button>
            </div>
          )}

          {businessIdea && !loading && !error && (
            <div className="mb-8">
              <BusinessIdeaCard businessIdea={businessIdea} />
            </div>
          )}

          {/* Empty State - Show when no business idea exists */}
          {!businessIdea && !loading && !error && renderEmptyState()}

          {/* Next Tasks Section - Only show when business idea exists */}
          {businessIdea && !loading && !error && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold font-heading text-white">What's Next?</h2>
                <button
                  onClick={() => navigate('/completed-tasks')}
                  className="text-text-secondary hover:text-white transition-colors text-sm flex items-center gap-1"
                >
                  <span>View Completed</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>

              <div className="bg-background-surface/95 backdrop-blur-sm rounded-lg border border-gray-600 p-6">
                {nextTasksLoading && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[...Array(6)].map((_, index) => (
                      <div
                        key={index}
                        className="bg-background-surface/95 backdrop-blur-sm rounded-lg border border-gray-600 p-4"
                      >
                        <div className="animate-pulse">
                          <div className="flex gap-2 mb-3">
                            <div className="h-5 bg-gray-700 rounded w-16"></div>
                            <div className="h-5 bg-gray-700 rounded w-20"></div>
                          </div>
                          <div className="h-5 bg-gray-700 rounded w-3/4 mb-2"></div>
                          <div className="h-4 bg-gray-700 rounded w-full mb-2"></div>
                          <div className="h-4 bg-gray-700 rounded w-5/6 mb-4"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {nextTasks.length > 0 && !nextTasksLoading && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {nextTasks.map((task) => (
                      <div key={task.taskId} onClick={() => handleTaskClick(task.taskId)}>
                        <TaskCard task={task} />
                      </div>
                    ))}
                  </div>
                )}

                {nextTasks.length === 0 && !nextTasksLoading && (
                  <div className="text-center py-8">
                    <div className="text-6xl mb-4">🎉</div>
                    <h3 className="text-xl font-semibold text-white mb-2">Congratulations!</h3>
                    <p className="text-text-secondary">
                      You've completed all the tasks in your startup journey. Your business is ready
                      to launch!
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default HomePage;
